import { api } from './client';

/**
 * `demo`: clinica demonstrativă, din ajutor și din datele ei, fără model;
 * `help`: o clinică fără AI, doar din ajutorul aplicației, fără model;
 * `live`: modelul. `off` nu vine de la server: e ce arată panoul cât timp
 * starea nu s-a putut citi.
 */
export type AppAssistantMode = 'live' | 'demo' | 'help' | 'off';

/**
 * Ce a pregătit asistentul pentru confirmare. `payload` e corpul cererii
 * obișnuite (la SMS are și `patientId`, care merge în cale); confirmarea o
 * trimite prin endpoint-ul obișnuit, apoi notează aici că s-a făcut.
 */
export interface AppAssistantProposal {
  /** Null pe clinica demonstrativă, unde nu se păstrează nimic. */
  id: string | null;
  kind: 'programare' | 'sarcina' | 'sms';
  summary: string;
  payload: Record<string, unknown>;
  warnings: string[];
  status: 'proposed' | 'done' | 'dismissed';
  resultId: string | null;
}

/** A patient the answer read; the panel offers to open their file inline. */
export interface AppAssistantPatientRef {
  id: string;
  name: string;
}

export interface AppAssistantMessage {
  id: string | null;
  role: 'user' | 'agent' | 'error';
  content: string;
  demo: boolean;
  createdAt: string;
  proposals: AppAssistantProposal[];
  patients?: AppAssistantPatientRef[];
}

export interface AppAssistantThread {
  id: string;
  title: string;
  updatedAt: string;
  messages: AppAssistantMessage[];
}

/** O intrare din fereastra „?": ce poate face asistentul, cu exemple. */
export interface AppAssistantCapability {
  kind: 'read' | 'action' | 'help';
  /** Substantivul scurt: „programările", „o programare". */
  label: string;
  /** Un rând despre ce se poate cere. */
  summary: string;
  /** Întrebări-exemplu, pe care omul le poate trimite cu un clic. */
  examples: string[];
}

export interface AppAssistantStatus {
  mode: AppAssistantMode;
  reason: string | null;
  suggestions: string[];
  /** Ce date poate citi asistentul pentru rolul acesta; gol = doar ajutorul. */
  reads: string[];
  /** Ce poate pregăti pentru confirmare; gol = nimic. */
  proposes: string[];
  /** Fereastra „?": tot ce poate face asistentul pentru rolul acesta. */
  capabilities: AppAssistantCapability[];
  limit: { perDay: number | null; usedToday: number };
  threads: AppAssistantThread[];
}

export interface AppAssistantAnswer {
  mode: AppAssistantMode;
  /** The conversation the answer belongs to; null on the demo clinic, where nothing is kept. */
  threadId: string | null;
  answer: AppAssistantMessage;
}

/** Cât model se cheltuie pe o întrebare: rapid, echilibrat sau profund. */
export type AssistantEffort = 'low' | 'mid' | 'high';

/** Asistentul virtual al aplicației. */
export const appAssistantApi = {
  status: (lang?: string) =>
    api.get<AppAssistantStatus>(
      lang ? `/app-assistant?lang=${encodeURIComponent(lang)}` : '/app-assistant',
    ),
  ask: (body: {
    question: string;
    threadId?: string;
    effort?: AssistantEffort;
    language?: string;
  }) => api.post<AppAssistantAnswer>('/app-assistant/ask', body),
  decide: (id: string, body: { status: 'done' | 'dismissed'; resultId?: string }) =>
    api.post<{ id: string; status: 'done' | 'dismissed'; resultId: string | null }>(
      `/app-assistant/proposals/${id}/decide`,
      body,
    ),
};
